/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
// Components
import {Box,
    Text,
    Flex,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import Breadcrumb from '../../../components/breadcrumb'
import Seo from '../../../components/seo'
import {useBrand} from '../../../contexts/brand-context'

const PageHeader = ({
    category,
    productSearchResult,
    searchQuery,
    ...otherProps
}) => {
    const {formatMessage} = useIntl()
    const {isGarage, brandId} = useBrand()
    const stylePlpSrp = useMultiStyleConfig('PlpSrpPages')

    // Some locale strings (e.g. fr-CA) interpolate the brand name via `{brandId}`,
    // so always supply it. Capitalize to match the convention used elsewhere
    // (e.g. GDITextBlock's brandName).
    const brandDisplayName = brandId
        ? brandId.charAt(0).toUpperCase() + brandId.slice(1)
        : ''

    const searchResultsPageTitle = isGarage
        ? formatMessage(
              {
                  id: 'search.results.page.title.garage',
                  defaultMessage: 'Search | Garage Clothing'
              },
              {brandId: brandDisplayName}
          )
        : formatMessage(
              {
                  id: 'search.results.page.title.dynamite',
                  defaultMessage: 'Search | Dynamite Clothing'
              },
              {brandId: brandDisplayName}
          )

    return (
        <Box {...otherProps} data-testid="sf-product-list-breadcrumb">
            {searchQuery && <Seo title={searchResultsPageTitle} />}
            {/* Breadcrumb */}
            <Breadcrumb categories={category?.parentCategoryTree} variant="plp"/>

            {/* Category / search title */}
            {searchQuery && (
                <Flex {...stylePlpSrp.headingContainer}>
                    <Flex gap={4} flexWrap="wrap">
                        <Text as="h1" {...stylePlpSrp.srpTitle}>
                            <FormattedMessage
                                defaultMessage="Search results"
                                id="search.result.heading"
                            />
                        </Text>
                    </Flex>

                    {productSearchResult?.total > 0 && (
                        <Text {...stylePlpSrp.srpSubtitle}>
                            {productSearchResult?.total > 0 ? (
                                <FormattedMessage
                                    defaultMessage='Showing {total} items for "{searchQuery}"'
                                    values={{total: productSearchResult?.total, searchQuery}}
                                    id="search_page.text.items"
                                />
                            ) : isGarage ? (
                                <FormattedMessage
                                    defaultMessage="Sorry! No items found. But here are some recommendations for you."
                                    id="search_page.text.no_items.garage"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Sorry! No items found. But here are some recommendations for you."
                                    id="search_page.text.no_items.dynamite"
                                />
                            )}
                        </Text>
                    )}
                </Flex>
            )}
        </Box>
    )
}

PageHeader.propTypes = {
    category: PropTypes.object,
    productSearchResult: PropTypes.object,
    searchQuery: PropTypes.string,
    total: PropTypes.number
}

export default PageHeader
